import { useDeferredValue, useMemo, useState, type ReactNode } from 'react';
import { Header } from '../components/Header';
import { ProductCard } from '../components/ProductCard';
import { useShop } from '../shop';

export function CataloguePage() {
  const { slug, shop, catalogue, session } = useShop();
  const [category, setCategory] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const q = useDeferredValue(query.trim().toLowerCase());

  const products = useMemo(() => {
    let list = catalogue.products;
    if (category !== null) list = list.filter((p) => p.categoryId === category);
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.sku?.toLowerCase().includes(q) ?? false) ||
          (p.description?.toLowerCase().includes(q) ?? false) ||
          p.variants.some((v) => v.name.toLowerCase().includes(q) || (v.sku?.toLowerCase().includes(q) ?? false)),
      );
    }
    return list;
  }, [catalogue.products, category, q]);

  // Only categories that have products: an empty chip is a dead end.
  const categories = useMemo(() => {
    const used = new Set(catalogue.products.map((p) => p.categoryId));
    return catalogue.categories.filter((c) => used.has(c.id));
  }, [catalogue]);

  const layout = shop.themePreset;
  const gridClass =
    layout === 'list' ? 'grid gap-2' : layout === 'cards' ? 'grid gap-3 sm:grid-cols-2 lg:grid-cols-3' : 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4';

  return (
    <>
      <Header />
      {shop.bannerUrl && (
        <div className="mx-auto max-w-5xl px-4 pt-4">
          <img src={shop.bannerUrl} alt="" className="aspect-[3/1] w-full rounded-2xl object-cover sm:aspect-[4/1]" />
        </div>
      )}
      <main className="mx-auto max-w-5xl px-4 pb-24 pt-4">
        {(shop.description || session.contactName) && (
          <div className="mb-4">
            {session.contactName && <p className="text-sm text-stone-500">Welcome back, {session.contactName}.</p>}
            {shop.description && <p className="text-stone-600 dark:text-stone-300">{shop.description}</p>}
          </div>
        )}

        {shop.orderingPaused && (
          <div className="mb-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-100 dark:ring-amber-900">
            {shop.pauseMessage || 'We are not taking orders right now. You can still browse.'}
          </div>
        )}

        {!session.pricesShown && (
          <div className="mb-4 rounded-xl bg-stone-100 px-4 py-3 text-sm text-stone-700 dark:bg-stone-900 dark:text-stone-300">
            Prices are shown to customers who open the shop from their WhatsApp link. Ask us in the chat for yours.
          </div>
        )}

        <div className="sticky top-14 z-10 -mx-4 bg-stone-50/95 px-4 py-2 backdrop-blur dark:bg-stone-950/95">
          <label className="relative block">
            <span className="sr-only">Search products</span>
            <svg className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${catalogue.products.length} products`}
              className="h-11 w-full rounded-full border border-stone-200 bg-white pl-10 pr-4 text-base text-stone-900 outline-none focus:border-[var(--brand)] dark:border-stone-800 dark:bg-stone-900 dark:text-stone-100"
            />
          </label>
          {categories.length > 0 && (
            <div className="-mx-4 mt-2 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none]">
              <Chip active={category === null} onClick={() => setCategory(null)}>All</Chip>
              {categories.map((c) => (
                <Chip key={c.id} active={category === c.id} onClick={() => setCategory(category === c.id ? null : c.id)}>
                  {c.name}
                </Chip>
              ))}
            </div>
          )}
        </div>

        {products.length === 0 ? (
          <div className="py-16 text-center text-stone-500">
            {catalogue.products.length === 0 ? 'Nothing is on the shelves yet.' : 'Nothing matches that. Try another word.'}
          </div>
        ) : (
          <div className={`mt-3 ${gridClass}`}>
            {products.map((p) => (
              <ProductCard key={p.id} product={p} slug={slug} layout={layout} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium transition ${
        active ? 'bg-[var(--brand)] text-white' : 'bg-white text-stone-700 ring-1 ring-stone-200 dark:bg-stone-900 dark:text-stone-200 dark:ring-stone-800'
      }`}
    >
      {children}
    </button>
  );
}

export function Footer() {
  const { shop } = useShop();
  return (
    <footer className="mx-auto max-w-5xl px-4 pb-8 text-xs text-stone-400">
      {shop.address && <p className="mb-1">{shop.address}</p>}
      <p>
        {shop.pricesIncludeTax ? 'Prices include tax.' : 'Prices exclude tax.'} Shop by {shop.name} · powered by PSM Softtech
      </p>
    </footer>
  );
}
